/**
 * OnboardingLayout - the kit's CenteredPage with this deployment's brand on top,
 * for every step taken before someone is in the workspace (sign-up, code,
 * login, accepting an invite, opening a shared link).
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { CenteredPage } from '../../../ui-kit';
import { config } from '../../../config';
import { ModeContext } from '../../../theme/ThemeProvider';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  maxWidth?: number;
  showLogo?: boolean;
  /** Optional e.g. "2 of 3" - shown as small stepper below logo */
  stepLabel?: string;
}

/**
 * The logo and name at the top of a page outside the app: the workspace's own
 * where its branding is known (a shared link, drawn inside `BrandScope`),
 * otherwise this deployment's.
 */
export function BrandLockup() {
  const { branding } = React.useContext(ModeContext);
  if (branding?.logoUrl && branding.logoType === 'wordmark') {
    // A wordmark already spells the name.
    return <Box component="img" src={branding.logoUrl} alt={branding.name} sx={{ height: 28, maxWidth: 180, display: 'block' }} />;
  }
  if (branding?.name) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {branding.logoUrl ? (
          <Box component="img" src={branding.logoUrl} alt=""
            sx={(t) => ({ width: 32, height: 32, borderRadius: `${t.ds.radius.sm}px` })} />
        ) : null}
        <Typography variant="h4" sx={(t) => ({ color: t.tokens.text.primary })}>{branding.name}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="img"
        src={config.faviconUrl}
        alt=""
        sx={(t) => ({ width: 32, height: 32, borderRadius: `${t.ds.radius.sm}px` })}
      />
      <Typography variant="h4" sx={(t) => ({ color: t.tokens.text.primary })}>{config.brandName}</Typography>
    </Box>
  );
}

export default function OnboardingLayout({ children, maxWidth = 520, showLogo = true, stepLabel }: OnboardingLayoutProps) {
  return (
    <CenteredPage brand={showLogo ? <BrandLockup /> : undefined} eyebrow={stepLabel} maxWidth={maxWidth}>
      {children}
    </CenteredPage>
  );
}
